import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import CustomSelect from "../../Components/common/CustomSelect/CustomSelect";
import CustomTable from "../../Components/common/Table/CustomTable";
import ListPopup from "../../Components/PagesComponents/Dashboard/ListPopup/ListPopup";
import ActionButton from "../../Components/common/ActionButton/ActionButton";
import FileIcon from "../../Components/Icon-SVG/FileIcon";
import QueueIcon from "../../Components/Icon-SVG/QueueIcon";
import DoCdr from "../../Components/common/DoCdr/DoCdr";
import {
  DetailEmpty,
  DetailField,
  DetailGrid,
  DetailItem,
  DetailNote,
  DetailPanel,
  DetailSection,
} from "../../Components/common/DetailPanel/DetailPanel";

import { analyzedMailsHeader } from "../../utils/constants/tableHeaders";
import { filterByDistinctGroupID, selectGroupApt, selectGroupSeverity } from "../../utils/function";
import { checkCdrFromExtension } from "./util";

import {
  RequestAnalyzedEmails,
  RequestDistinctHosts,
} from "../../store/actions/mailGatewayActions";

import "./styles.scss";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const Verdict = ({ submissions, groupID }) => {
  const decision = selectGroupSeverity(submissions, groupID);
  const { isApt, aptClasses } = selectGroupApt(submissions, groupID);

  if (decision === "malicious") {
    return (
      <div className="verdict-container">
        <div className={`verdict verdict--${isApt ? "apt" : "malicious"}`}>
          {aptClasses.length > 0
            ? aptClasses
                .slice(0, 2)
                .map((className) => (
                  <p key={className}>
                    {className.length > 11 ? `#${className.slice(0, 11)}...` : `#${className}`}
                  </p>
                ))
            : "Malicious"}
        </div>
      </div>
    );
  } else if (decision === "suspicious") {
    return (
      <div className="verdict-container">
        <p className="verdict verdict--suspicious">Suspicious</p>
      </div>
    );
  } else if (decision === "not suspicious") {
    return (
      <div className="verdict-container">
        <p className="verdict verdict--clean">Not Suspicious</p>
      </div>
    );
  } else if (decision === "loading") {
    return (
      <div className="verdict-container">
        <span className="verdict">
          <QueueIcon width={20} height={20} />
        </span>
      </div>
    );
  } else if (decision === "n/a") {
    return (
      <div className="verdict-container">
        <span className="verdict">N/A</span>
      </div>
    );
  }
};

const Details = ({ row, history }) => {
  const submissions = row?.original?.submissions || [];

  const filteredSubmissions = submissions.filter((el, i, arr) =>
    filterByDistinctGroupID(el, i, arr),
  );

  const seeReportHandler = (groupID) => {
    history.push(`/mailgateway/analyzed-emails?data=${groupID}&table=analyzed-emails`);
  };

  return (
    <DetailPanel>
      <DetailSection title="Analysed attachments" count={filteredSubmissions.length}>
        {filteredSubmissions.length ? (
          filteredSubmissions.map((submission, i) => (
            <DetailItem
              key={i}
              actions={
                <>
                  <ActionButton
                    icon={<FileIcon width={13} height={13} color="currentColor" />}
                    onClick={() => seeReportHandler(submission.groupID)}>
                    See Report
                  </ActionButton>
                  {checkCdrFromExtension(submission?.file_name?.split(".")?.pop()) ? (
                    <DoCdr
                      buttonVariant="action"
                      fileHash={submission?.file_hash}
                      fileId={submission?.fileID}
                      marginal={"0em"}
                      severity={submission.decision === 0 ? "not suspicious" : "malicious"}
                      fileExt={submission?.file_name?.split(".")?.pop()}
                    />
                  ) : null}
                </>
              }>
              <DetailGrid columns={3}>
                <DetailField label="File name">{submission?.file_name}</DetailField>
                <DetailField label="SHA256" mono>
                  {submission?.file_hash}
                </DetailField>
                <DetailField label="Verdict">
                  <Verdict submissions={submissions} groupID={submission.groupID} />
                </DetailField>
              </DetailGrid>
            </DetailItem>
          ))
        ) : (
          <DetailEmpty>No attachments were analysed for this email.</DetailEmpty>
        )}
      </DetailSection>
    </DetailPanel>
  );
};

const AnalyzedEmails = () => {
  const [selectedHost, setSelectedHost] = useState(null);
  const [listPopupActive, setListPopupActive] = useState(false);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState(null);

  const dispatch = useDispatch();

  const distinctHosts = useSelector((state) => state.mailGateway.distinctHosts);
  const distinctHostsLoading = useSelector((state) => state.mailGateway.distinctHostsLoading);
  const analyzedEmails = useSelector((state) => state.mailGateway.analyzedEmails);
  const analyzedEmailsLoading = useSelector((state) => state.mailGateway.analyzedEmailsLoading);

  let query = useQuery();

  useEffect(() => {
    if (query.get("data") !== null) {
      setListPopupActive(true);
    } else {
      setListPopupActive(false);
    }
  }, [query]);

  useEffect(() => {
    dispatch(RequestDistinctHosts());
  }, [dispatch]);

  useEffect(() => {
    if (selectedHost?.id && selectedTimeFilter) {
      dispatch(RequestAnalyzedEmails({ host: selectedHost.id, timeFilter: selectedTimeFilter }));
    }
  }, [dispatch, selectedHost, selectedTimeFilter]);

  return (
    <div className="analyzed-emails">
      <div className="analyzed-emails__header">
        <h1>Analyzed Emails</h1>
        <p>Below list is displaying analyzed emails of selected host:</p>
        <div className="analyzed-emails__header__select">
          <p>Select host: </p>
          <CustomSelect
            id="mgwAnalyzedEmails"
            data={distinctHosts.length ? distinctHosts : []}
            select={selectedHost}
            setSelected={setSelectedHost}
            width={220}
            disabled={distinctHosts.length === 0}
            placeholder={distinctHostsLoading ? "Fetching data..." : "No data."}
          />
        </div>
      </div>
      <div className="analyzed-emails__table border">
        <CustomTable
          exportFile={true}
          dataTable={analyzedEmails}
          loading={analyzedEmailsLoading}
          tableName="analyzedEmails"
          column={analyzedMailsHeader}
          leftAction={true}
          paginationButtons={true}
          className=""
          customPageSize={10}
          renderRowSubComponent={Details}
          minHeight="585px"
          minBottom="5px"
          defaultPosition="relative"
          selectedHost={selectedHost?.id}
          filterByTime={true}
          selectedTimeFilter={selectedTimeFilter}
          setSelectedTimeFilter={setSelectedTimeFilter}
          defaultFilterIndex={2}
        />
      </div>
      <ListPopup
        onClickOk={() => setListPopupActive(false)}
        show={listPopupActive}
        groupID={query.get("data")}
        tableType={query.get("table")}
        tableType2="analyzed-emails"
      />
    </div>
  );
};

export default AnalyzedEmails;
